import React, { useEffect, useRef, useMemo, useCallback } from 'react'
import { message } from 'antd'
import { Pagination } from '@surecompanies/core_components'
import { ColDef, ModuleRegistry } from '@ag-grid-community/core'
import type { RowGroupOpenedEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { VCN_SELECTION_MAX } from '../../api/dto/vcn-selection.dto'
import type { PaginationState, VcnManagementBenefitRow } from './types'
import {
  BenefitIdCellRenderer,
  InsuredPersonCellRenderer,
  PrimaryPaymentMethodCellRenderer,
  VcnProviderCellRenderer,
  VcnStatusCellRenderer,
  VcnTokenCellRenderer,
} from './VcnManagementTableCellTags'
import AssociatedVcnsPanel from './AssociatedVcnsPanel'
import {
  GridViewport,
  PaginationContainer,
  TableShell,
} from './VcnManagementLayout.styles'
import { useFitViewportHeight } from './hooks/useFitViewportHeight'

ModuleRegistry.registerModules([MasterDetailModule])

const tagCellStyle = {
  display: 'flex',
  alignItems: 'center',
} as const

export interface VcnManagementBenefitTableProps {
  rows: VcnManagementBenefitRow[]
  emptyText: React.ReactNode
  selectedRows: VcnManagementBenefitRow[]
  selectedRowKeys: string[]
  pagination: PaginationState
  onRowSelectionChange: (rows: VcnManagementBenefitRow[]) => void
  onPaginationChange: (page: number, pageSize: number) => void
}

export default function VcnManagementBenefitTable({
  rows,
  emptyText,
  selectedRows,
  selectedRowKeys,
  pagination,
  onRowSelectionChange,
  onPaginationChange,
}: VcnManagementBenefitTableProps) {
  const defaultColDef = useMemo<ColDef<VcnManagementBenefitRow>>(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  )

  const columnDefs = useMemo<ColDef<VcnManagementBenefitRow>[]>(
    () => [
      {
        headerName: '',
        width: 52,
        pinned: 'left',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        suppressMovable: true,
        sortable: false,
      },
      {
        field: 'companyName',
        headerName: 'Company',
        flex: 1,
        minWidth: 160,
        // Hosts the master-detail expand caret for the "Associated VCNs" row.
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: 'carrierName',
        headerName: 'Carrier',
        width: 130,
      },
      {
        field: 'benefitId',
        headerName: 'Benefit ID',
        width: 160,
        cellStyle: tagCellStyle,
        cellRenderer: BenefitIdCellRenderer,
      },
      {
        field: 'insuredPersonName',
        headerName: 'Insured person',
        flex: 1,
        minWidth: 150,
        cellRenderer: InsuredPersonCellRenderer,
      },
      {
        field: 'primaryPaymentMethod',
        headerName: 'Payment method',
        width: 140,
        cellStyle: tagCellStyle,
        cellRenderer: PrimaryPaymentMethodCellRenderer,
      },
      {
        field: 'provider',
        headerName: 'Provider',
        width: 130,
        cellStyle: tagCellStyle,
        cellRenderer: VcnProviderCellRenderer,
      },
      {
        field: 'vcnStatus',
        headerName: 'VCN status',
        width: 130,
        cellStyle: tagCellStyle,
        cellRenderer: VcnStatusCellRenderer,
      },
      {
        field: 'vcnToken',
        headerName: 'VCN token',
        flex: 1,
        minWidth: 140,
        cellRenderer: VcnTokenCellRenderer,
      },
      {
        field: 'lastFourDigits',
        headerName: 'Last 4',
        width: 90,
      },
    ],
    []
  )

  const gridRef = useRef<AgGridReact<VcnManagementBenefitRow>>(null)

  // Pagination sits below the grid inside the shell, so the shell is what has
  // to end at the viewport bottom — the grid then flexes into what is left.
  // Layout above the table (banners, alerts, wrapping toolbar) is watched by
  // the hook; do not list those causes here.
  const { setNode: setShellNode, height: shellHeight } = useFitViewportHeight()

  // A row is expandable once we know its employee (keys the sub-table fetch).
  const isRowMaster = useCallback(
    (data: VcnManagementBenefitRow) => Boolean(data?.employeePublicKey),
    []
  )

  // Keep at most one detail row open: the card action bar acts on a single
  // employee's selection, so collapse any other expanded row when one opens.
  const handleRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<VcnManagementBenefitRow>) => {
      if (!event.node.expanded) return
      event.api.forEachNode((node) => {
        if (node.expanded && node.id !== event.node.id) {
          node.setExpanded(false)
        }
      })
    },
    []
  )

  const hasSameSelection = useCallback(
    (currentSelection: string[], nextSelection: string[]) => {
      if (currentSelection.length !== nextSelection.length) return false
      const set = new Set(currentSelection)
      if (set.size !== currentSelection.length) return false
      return nextSelection.every((key) => set.has(key))
    },
    []
  )

  useEffect(() => {
    if (!gridRef.current?.api) return
    const selected = new Set(selectedRowKeys)
    gridRef.current.api.forEachNode((node) => {
      const id = node.data?.benefitId ?? ''
      const shouldSelect = selected.has(id)
      if (node.isSelected() !== shouldSelect) {
        node.setSelected(shouldSelect, false)
      }
    })
  }, [selectedRowKeys, rows])

  const handleSelectionChanged = useCallback(() => {
    const selectedOnPage = gridRef.current?.api?.getSelectedRows() ?? []
    const currentPageIds = new Set(rows.map((r) => r.benefitId))
    const otherPagesRows = selectedRows.filter(
      (r) => !currentPageIds.has(r.benefitId)
    )
    const merged = [...otherPagesRows, ...selectedOnPage]

    if (merged.length > VCN_SELECTION_MAX) {
      const available = VCN_SELECTION_MAX - otherPagesRows.length
      const trimmedPage = selectedOnPage.slice(0, Math.max(0, available))
      const allowed = new Set(trimmedPage.map((r) => r.benefitId))
      gridRef.current?.api?.forEachNode((node) => {
        if (node.isSelected() && !allowed.has(node.data?.benefitId ?? '')) {
          node.setSelected(false, false)
        }
      })
      void message.warning(
        `Selection is limited to ${VCN_SELECTION_MAX} items. Extra rows were deselected.`
      )
      onRowSelectionChange([...otherPagesRows, ...trimmedPage])
      return
    }

    const nextKeys = merged.map((r) => r.benefitId)
    if (hasSameSelection(selectedRowKeys, nextKeys)) return
    onRowSelectionChange(merged)
  }, [
    hasSameSelection,
    onRowSelectionChange,
    selectedRowKeys,
    rows,
    selectedRows,
  ])

  return (
    <TableShell innerRef={setShellNode} style={{ height: shellHeight }}>
      <GridViewport className="ag-theme-alpine">
        <AgGridReact<VcnManagementBenefitRow>
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data?.benefitId ?? ''}
          rowSelection="multiple"
          onSelectionChanged={handleSelectionChanged}
          masterDetail
          isRowMaster={isRowMaster}
          detailCellRenderer={AssociatedVcnsPanel}
          detailRowAutoHeight
          onRowGroupOpened={handleRowGroupOpened}
          suppressRowClickSelection
          suppressCellFocus
          headerHeight={44}
          noRowsOverlayComponent={() => (
            <div className="ag-overlay-no-rows-center" style={{ padding: 16 }}>
              {emptyText}
            </div>
          )}
        />
      </GridViewport>
      {pagination.total > 0 ? (
        <PaginationContainer>
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.pageSize}
            pageSizeOptions={[25, 50, 100, 200]}
            showSizeChanger
            showTotal={(total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} rows`
            }
            onChange={onPaginationChange}
          />
        </PaginationContainer>
      ) : null}
    </TableShell>
  )
}
